// services/frontend/src/utils/guestSignalLedger.js

export function getGuestSignalEvents() {
  try {
    return JSON.parse(localStorage.getItem('togeevision_guest_signal_events') || '[]');
  } catch {
    return [];
  }
}

export function saveGuestSignalEvents(events) {
  try {
    localStorage.setItem('togeevision_guest_signal_events', JSON.stringify(events));
  } catch (err) {
    console.error('[guestSignalLedger] failed to save guest events:', err);
  }
}

export function emitGuestSignalUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('togeevision:guest-signal-updated'));
  }
}

export function getGuestPassportStamps() {
  const guestEvents = getGuestSignalEvents();
  return guestEvents.map(evt => evt.passportStamp);
}

export function hasRecentScan({ artistSlug, gigId, cityId }) {
  const resolvedGigId = gigId || 'artist-level';
  const dedupeKey = `togeevision_scan_claimed:${artistSlug}:${resolvedGigId}:${cityId}`;
  try {
    const lastScanTime = localStorage.getItem(dedupeKey);
    if (!lastScanTime) return false;
    const now = Date.now();
    return (now - parseInt(lastScanTime, 10) < 15 * 60 * 1000); // 15 minutes dedupe window
  } catch {
    return false;
  }
}

function getSignalSessionId() {
  const storageKey = 'togeevision.signal-session-id';
  try {
    const existing = localStorage.getItem(storageKey);
    if (existing) return existing;
    const created = globalThis.crypto?.randomUUID?.()
      || `signal-session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(storageKey, created);
    return created;
  } catch {
    return `signal-session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export async function createSignalEvent(scanContext, user = null) {
  const { artistSlug, artistId, cityId, gigId, source, venueId, stampText, campaignId } = scanContext;
  const resolvedGigId = gigId || 'artist-level';
  const resolvedVenueId = venueId || (gigId?.includes('kismat') ? 'venue-kismat-washington-heights' : (gigId?.includes('lv') ? 'venue_steelstacks' : 'venue-place-des-festivals'));
  const resolvedSource = source || 'artist_qr';
  const resolvedCampaignId = campaignId || 'none';
  const resolvedArtistId = artistId || `artist_${artistSlug.replace(/-/g, '_')}`;
  const isAuthenticated = Boolean(user?.uid);

  // 1. Generate local fallback keys
  const fallbackId = `sig_evt_${Date.now()}_fallback`;
  const fallbackToken = 'local_fallback_secret';

  // 2. Add to active signal events locally first (so MyPassport/React updates instantly)
  const signalEvent = {
    id: fallbackId,
    artistSlug,
    artistId: resolvedArtistId,
    gigNodeId: resolvedGigId,
    cityId,
    venueId: resolvedVenueId,
    source: resolvedSource,
    campaignId: resolvedCampaignId,
    actionType: isAuthenticated ? 'presence_recorded' : 'guest_presence_recorded',
    authState: isAuthenticated ? 'authenticated' : 'guest',
    fanId: isAuthenticated ? user.uid : null,
    passportStamp: stampText || 'LV 2026',
    tokenType: 'frequency_token',
    tokenMinted: true,
    radarDelta: 1,
    createdAt: new Date().toISOString(),
    pendingClaim: false,
    claimToken: isAuthenticated ? null : fallbackToken
  };

  try {
    const activeEvents = JSON.parse(localStorage.getItem('gigPassport.signalEvents') || '[]');
    activeEvents.unshift(signalEvent);
    localStorage.setItem('gigPassport.signalEvents', JSON.stringify(activeEvents));

    if (!isAuthenticated) {
      const guestEvents = getGuestSignalEvents();
      guestEvents.unshift(signalEvent);
      saveGuestSignalEvents(guestEvents);
    }

    localStorage.setItem(`gigPassport.presence.${cityId}`, 'true');
    localStorage.setItem('gigPassport.city', cityId);

    const dedupeKey = `togeevision_scan_claimed:${artistSlug}:${resolvedGigId}:${cityId}`;
    localStorage.setItem(dedupeKey, Date.now().toString());

    emitGuestSignalUpdated();
  } catch (err) {
    console.error('[guestSignalLedger] failed local storage save:', err);
  }

  // 3. Dispatch to backend API in background to get server-verified ID and claim secret
  try {
    const idToken = isAuthenticated ? await user.getIdToken() : null;
    const res = await fetch('/api/gig-passport/signal-event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
      },
      body: JSON.stringify({
        artistSlug,
        cityId,
        gigNodeId: resolvedGigId,
        venueId: resolvedVenueId,
        source: resolvedSource,
        campaignId: resolvedCampaignId,
        guestSessionIdHash: isAuthenticated ? undefined : getSignalSessionId()
      })
    });
    const data = await res.json();
    if (data.ok && data.isDuplicate) {
      const activeEvents = JSON.parse(localStorage.getItem('gigPassport.signalEvents') || '[]');
      localStorage.setItem(
        'gigPassport.signalEvents',
        JSON.stringify(activeEvents.filter(evt => evt.id !== fallbackId))
      );
      if (!isAuthenticated) {
        saveGuestSignalEvents(getGuestSignalEvents().filter(evt => evt.id !== fallbackId));
      }
      emitGuestSignalUpdated();
      return null;
    }
    if (data.ok && data.signalEventId && (isAuthenticated || data.claimToken)) {
      // Update local entries with server-verified values
      const activeEvents = JSON.parse(localStorage.getItem('gigPassport.signalEvents') || '[]');
      const idx1 = activeEvents.findIndex(evt => evt.id === fallbackId);
      if (idx1 !== -1) {
        activeEvents[idx1].id = data.signalEventId;
        activeEvents[idx1].claimToken = isAuthenticated ? null : data.claimToken;
        if (data.receipt) activeEvents[idx1].receipt = data.receipt;
        localStorage.setItem('gigPassport.signalEvents', JSON.stringify(activeEvents));
      }

      if (!isAuthenticated) {
        const guestEvents = getGuestSignalEvents();
        const idx2 = guestEvents.findIndex(evt => evt.id === fallbackId);
        if (idx2 !== -1) {
          guestEvents[idx2].id = data.signalEventId;
          guestEvents[idx2].claimToken = data.claimToken;
          if (data.receipt) guestEvents[idx2].receipt = data.receipt;
          saveGuestSignalEvents(guestEvents);
        }
      }

      if (data.receipt) {
        signalEvent.receipt = data.receipt;
      }
      emitGuestSignalUpdated();
    }
  } catch (err) {
    console.warn('[guestSignalLedger] background server sync failed (operating in offline fallback):', err);
  }

  return signalEvent;
}

export function createGuestSignalEvent(scanContext) {
  return createSignalEvent(scanContext);
}

export function markGuestSignalsPendingClaim() {
  const guestEvents = getGuestSignalEvents();
  if (guestEvents.length === 0) return;
  const updated = guestEvents.map(evt => ({ ...evt, pendingClaim: true }));
  saveGuestSignalEvents(updated);
}

export async function claimGuestSignalsForUser(user) {
  if (!user) return false;
  try {
    const guestEvents = getGuestSignalEvents();
    const pendingClaimEvents = guestEvents.filter(evt => evt.pendingClaim);
    if (pendingClaimEvents.length === 0) return false;

    // Send payload to backend
    const claims = pendingClaimEvents.map(evt => ({
      signalEventId: evt.id,
      claimToken: evt.claimToken || 'local_fallback_secret'
    }));

    const idToken = await user.getIdToken();
    const res = await fetch('/api/gig-passport/claim-guest-signals', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      },
      body: JSON.stringify({ claims })
    });
    const data = await res.json();

    if (data.ok) {
      // Migrate locally
      const activeEvents = JSON.parse(localStorage.getItem('gigPassport.signalEvents') || '[]');
      const newActiveEvents = activeEvents.map(evt => {
        const matched = pendingClaimEvents.find(pe => pe.id === evt.id);
        if (matched) {
          return {
            ...evt,
            authState: 'authenticated',
            fanId: user.uid,
            pendingClaim: false
          };
        }
        return evt;
      });

      localStorage.setItem('gigPassport.signalEvents', JSON.stringify(newActiveEvents));

      const remainingGuestEvents = guestEvents.filter(evt => !evt.pendingClaim);
      saveGuestSignalEvents(remainingGuestEvents);

      emitGuestSignalUpdated();
      return true;
    }
    return false;
  } catch (err) {
    console.error('[guestSignalLedger] failed to sync claims to server:', err);
    return false;
  }
}

export async function claimAllGuestSignalsDirectly(user) {
  if (!user) return false;
  try {
    const guestEvents = getGuestSignalEvents();
    if (guestEvents.length === 0) return false;

    const claims = guestEvents.map(evt => ({
      signalEventId: evt.id,
      claimToken: evt.claimToken || 'local_fallback_secret'
    }));

    const idToken = await user.getIdToken();
    const res = await fetch('/api/gig-passport/claim-guest-signals', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      },
      body: JSON.stringify({ claims })
    });
    const data = await res.json();

    if (data.ok) {
      const activeEvents = JSON.parse(localStorage.getItem('gigPassport.signalEvents') || '[]');
      const newActiveEvents = activeEvents.map(evt => {
        const isGuestMatch = guestEvents.some(ge => ge.id === evt.id);
        if (isGuestMatch) {
          return {
            ...evt,
            authState: 'authenticated',
            fanId: user.uid,
            pendingClaim: false
          };
        }
        return evt;
      });

      localStorage.setItem('gigPassport.signalEvents', JSON.stringify(newActiveEvents));
      saveGuestSignalEvents([]);
      emitGuestSignalUpdated();
      return true;
    }
    return false;
  } catch (err) {
    console.error('[guestSignalLedger] failed to claim guest signals directly:', err);
    return false;
  }
}

export async function dismissAllGuestSignals() {
  try {
    const guestEvents = getGuestSignalEvents();
    if (guestEvents.length > 0) {
      const claims = guestEvents.map(evt => ({
        signalEventId: evt.id,
        claimToken: evt.claimToken || 'local_fallback_secret'
      }));
      await fetch('/api/gig-passport/dismiss-guest-signals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ claims })
      });
    }
  } catch (err) {
    console.warn('[guestSignalLedger] failed to dismiss guest signals on server:', err);
  }
  saveGuestSignalEvents([]);
  emitGuestSignalUpdated();
}
